import { Component, Inject } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';

import { IoService } from '../../../services/io/io.service';
import { AppComponent } from '../../../app.component';

// Merge two customer profiles — or a customer into a staff user: search for
// the other record, compare side by side, choose which profile survives,
// confirm. Staff candidates always survive (the server only accepts customer
// sources), so the keep choice locks when a staff record is selected.
// Requires the mergeCustomers permission (plus manageUsers for staff targets,
// both enforced server-side in /customer/mergeCustomers).
@Component({
  selector: 'app-customer-merge',
  templateUrl: './customer-merge.component.html',
  styleUrls: ['./customer-merge.component.scss']
})
export class CustomerMergeComponent {

  customer: any;
  searchText = '';
  searching = false;
  results: any = null;
  candidate: any = null;
  keep: 'current' | 'candidate' = 'current';
  merging = false;
  error: string = null;

  private searchTimeout;

  public static showModal(customer: any): MatDialogRef<CustomerMergeComponent> {
    return AppComponent.dialog.open(CustomerMergeComponent, {
      width: '860px',
      maxWidth: '95vw',
      panelClass: 'opaque-dialog',
      hasBackdrop: true,
      disableClose: false,
      data: {
        customer,
      },
    });
  }

  constructor(
    private ioService: IoService,
    private dialogRef: MatDialogRef<CustomerMergeComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
  ) {
    this.customer = data.customer;

    // Duplicates usually share the name, so start with the current profile's
    // name already searched.
    this.searchText = [this.customer.firstName, this.customer.lastName]
      .filter((part) => part)
      .join(' ')
      .trim() || this.customer.email || this.customer.telephone || '';
    this.searchChanged();
  }

  searchChanged() {
    this.error = null;
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout);
    }

    const search = (this.searchText || '').trim();
    if (search.length < 2) {
      this.results = null;
      this.searching = false;
      return;
    }

    this.searching = true;
    this.searchTimeout = setTimeout(async () => {
      try {
        this.results = await this.ioService.post('/customer/findMergeCandidates', {
          search,
          excludeUserId: this.customer._id,
          includeUsers: true,
        });
      } catch (err) {
        this.results = [];
        this.error = err.error?.msg || 'Search failed.';
      }
      this.searching = false;
    }, 400);
  }

  get candidateIsStaff(): boolean {
    return this.candidate && this.candidate.type === 'user';
  }

  selectCandidate(candidate) {
    this.candidate = candidate;
    // A staff record can only be the survivor — the customer merges into it.
    this.keep = candidate && candidate.type === 'user' ? 'candidate' : 'current';
    this.error = null;
  }

  setKeep(which: 'current' | 'candidate') {
    if (this.candidateIsStaff && which === 'current') {
      return;
    }
    this.keep = which;
  }

  clearCandidate() {
    this.candidate = null;
  }

  async confirmMerge() {
    if (!this.candidate || this.merging) {
      return;
    }
    if (this.candidateIsStaff) {
      this.keep = 'candidate';
    }

    const targetUserId = this.keep === 'current' ? this.customer._id : this.candidate._id;
    const sourceUserId = this.keep === 'current' ? this.candidate._id : this.customer._id;

    this.merging = true;
    this.error = null;
    try {
      await this.ioService.post('/customer/mergeCustomers', {
        sourceUserId,
        targetUserId,
      });
      this.dialogRef.close({ merged: true, targetUserId });
    } catch (err) {
      this.error = err.error?.msg || 'Unable to merge customer profiles.';
      this.merging = false;
    }
  }

  cancel() {
    this.dialogRef.close(null);
  }
}
